import React, {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import clsx from "clsx";
import { logError } from "utility/Function";
import styles from "./SkyBackground.module.scss";

// The package carries the whole WebGL runtime inline, so loading it eagerly
// would put roughly a quarter of a megabyte, gzipped, in front of every
// learner to serve a screen almost none of them reach. Imported here instead,
// so the chunk is fetched from our own origin only once a sky is actually
// asked for.
//
// Tearing the scene down is the wrapper's job, not ours: its own effect
// cleanup calls the scene's `destroy()`, so unmounting this component stops
// the render loop. There is deliberately no `destroy()` call here — a second
// one would be destroying a scene we do not hold a handle to.
//
// The scene's data comes with it: our own copy of the published scene
// (Unicorn Studio project Ka7o36AXbV4Mrp4a1g9T), committed in
// frontend/packages/assets. With the runtime bundled too, the sky reaches
// nothing outside our own deploy. It goes in as a data URL because the SDK
// takes a scene only by URL.
const UnicornScene = React.lazy(async () => {
  const [{ default: Scene }, { default: sceneData }] = await Promise.all([
    import("unicornstudio-react"),
    import("../../../../packages/assets/report-sky/scene.json"),
  ]);
  const sceneUrl = `data:application/json,${encodeURIComponent(
    JSON.stringify(sceneData)
  )}`;
  const OwnScene = (
    props: Omit<
      React.ComponentProps<typeof Scene>,
      "projectId" | "jsonFilePath"
    >
  ) => <Scene {...props} jsonFilePath={sceneUrl} />;
  return { default: OwnScene };
});

// How long the scene gets to say anything at all before its silence is
// treated as a failure. A chunk request that hangs rather than rejects, or an
// SDK that never finishes building the scene, leaves both callbacks unfired:
// no error, no ready, just the gradient. Generous enough not to libel a slow
// connection, short enough that the report is still on screen when it fires.
const SCENE_READY_TIMEOUT_MS = 15000;

// How long the scene draws, hidden, before it is shown. Its `onLoad` fires
// when the SDK has built the scene, not when the scene looks like a sky: on a
// GPU that compiles shaders in parallel, the first frames skip the passes
// still compiling, and the blur and noise that turn two shapes into clouds
// arrive last. Shown at load, those frames read as a pale field of blue dots
// laid over the poster. So the still holds until the scene has been drawing
// for a while, measured both in time, which covers the compile, and in
// frames, so a tab in the background, which draws nothing, never counts as
// settled.
const SCENE_SETTLE_MS = 1500;
const SCENE_SETTLE_FRAMES = 30;

// True once the scene has loaded and then drawn for the settle above. Resets
// when the scene stops being loaded.
const useSceneSettled = (loaded: boolean): boolean => {
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    if (!loaded) {
      setSettled(false);
      return undefined;
    }
    const start = performance.now();
    let frames = 0;
    let request = 0;
    const tick = () => {
      frames += 1;
      if (
        frames >= SCENE_SETTLE_FRAMES &&
        performance.now() - start >= SCENE_SETTLE_MS
      ) {
        setSettled(true);
        return;
      }
      request = requestAnimationFrame(tick);
    };
    request = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(request);
  }, [loaded]);

  return settled;
};

// A scene chunk that never arrives throws while rendering, and the app has no
// error boundary of its own, so the throw would unmount the whole tree and
// leave a blank page seconds after someone finished their assessment. This
// keeps a backdrop failure to the backdrop.
class SceneBoundary extends React.Component<
  { onFailure: (error: Error) => void; children: React.ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error) {
    this.props.onFailure(error);
  }

  render() {
    return this.state.failed ? null : this.props.children;
  }
}

// The scene's shaders are all `#version 300 es`, which only WebGL2 compiles.
// The probe is a real GPU context: while the scene draws, creating one is a
// synchronous round trip of around 100ms, and the browser evicts the oldest
// contexts once about sixteen are live. So it is released as soon as it has
// answered, and asked once per mount rather than on every render.
const supportsWebGl2 = (): boolean => {
  try {
    const probe = document.createElement("canvas").getContext("webgl2");
    probe?.getExtension("WEBGL_lose_context")?.loseContext();
    return Boolean(probe);
  } catch {
    return false;
  }
};

const prefersReducedMotion = (): boolean =>
  Boolean(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);

// The SDK labels its own canvas "Unicorn Studio Scene", and reads the option
// that would override it as `ariaLabel || "Unicorn Studio Scene"` — so an
// empty string cannot win. `aria-hidden` on an ancestor does not suppress it
// either (verified in Chrome's accessibility tree), so the attributes come
// off the canvas itself once the SDK has created it.
const hideSceneFromAssistiveTech = (container: HTMLElement | null): void => {
  container?.querySelectorAll("canvas").forEach((canvas) => {
    canvas.removeAttribute("aria-label");
    canvas.setAttribute("role", "presentation");
    canvas.setAttribute("aria-hidden", "true");
  });
};

// A lost WebGL context blanks the canvas for good: nothing re-creates it, and
// the wrapper does not route the loss through its own `onError`, so the scene
// goes on claiming it is running over a canvas that draws nothing. The browser
// tells the canvas directly, which is why the listener goes there rather than
// on a prop. Returns its own undo, since the canvas outlives this call.
const watchForContextLoss = (
  container: HTMLElement | null,
  onLost: () => void
): (() => void) => {
  const canvases = Array.from(container?.querySelectorAll("canvas") ?? []);
  canvases.forEach((canvas) =>
    canvas.addEventListener("webglcontextlost", onLost)
  );
  return () =>
    canvases.forEach((canvas) =>
      canvas.removeEventListener("webglcontextlost", onLost)
    );
};

export interface SkyBackgroundProps {
  className?: string;
}

/**
 * The report's moving sky, behind whatever is placed on top of it.
 *
 * The gradient underneath is not just a fallback: it paints on the first
 * frame and stays there, so the scene fades in over the right colours
 * rather than over nothing. If WebGL2 is missing, the viewer asked for
 * reduced motion, or the scene fails to start or stops drawing, the gradient
 * is all that shows. Every part of it is dark enough for the white the
 * report is written in, so losing the sky costs the screen its atmosphere
 * and never its copy.
 *
 * Between the two, until the scene has settled into its clouds, sits a
 * poster frame of the sky from design. The scene takes a few seconds to load its runtime and its
 * data, and without the still the report opens on flat blue and only then
 * becomes a sky. The scene fades in over a picture of itself, so the
 * handover reads as the sky starting to move. Mobile does the same.
 */
const SkyBackground: React.FC<SkyBackgroundProps> = ({ className }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  // The SDK has built the scene. Not yet shown: see SCENE_SETTLE_MS.
  const [sceneLoaded, setSceneLoaded] = useState(false);
  const sceneReady = useSceneSettled(sceneLoaded);
  // Takes the still away with the scene, so a scene that has given up
  // leaves the gradient rather than a picture of a sky that is not coming.
  const [sceneFailed, setSceneFailed] = useState(false);
  // One report per mount, whichever way the scene fails. Without this the
  // timeout could fire after an error had already been reported, and the
  // same broken backdrop would arrive twice under different descriptions.
  const reportedRef = useRef(false);
  // Undoes the context-loss listener. Held rather than returned from an
  // effect because the canvas it attaches to does not exist until the scene
  // says it has loaded.
  const stopWatchingRef = useRef<() => void>();

  // Per mount rather than per module, so each mount asks the browser it is
  // in now, not whatever it answered the first time.
  const [staticOnly] = useState(
    () => supportsWebGl2() === false || prefersReducedMotion()
  );

  const report = useCallback((message: string, error: Error) => {
    if (reportedRef.current) return;
    reportedRef.current = true;
    logError(message, error);
  }, []);

  const onContextLost = useCallback(() => {
    report(
      "The sky background scene's WebGL context was lost",
      new Error("Scene canvas lost its WebGL context and stopped drawing")
    );
    setSceneLoaded(false);
    setSceneFailed(true);
  }, [report]);

  const onLoad = useCallback(() => {
    hideSceneFromAssistiveTech(containerRef.current);
    stopWatchingRef.current?.();
    stopWatchingRef.current = watchForContextLoss(
      containerRef.current,
      onContextLost
    );
    setSceneLoaded(true);
  }, [onContextLost]);

  useEffect(() => () => stopWatchingRef.current?.(), []);

  const onError = useCallback(
    (error: Error) => {
      report("Could not start the sky background scene", error);
      setSceneLoaded(false);
      setSceneFailed(true);
    },
    [report]
  );

  const onSceneChunkFailure = useCallback(
    (error: Error) => {
      report("Could not load the sky background scene's code", error);
      setSceneLoaded(false);
      setSceneFailed(true);
    },
    [report]
  );

  // A scene that has said nothing by now is not going to. The gradient
  // underneath is a convincing enough backdrop that a dead scene looks
  // deliberate, so without this the only way to notice is for someone to go
  // and look at the screen.
  useEffect(() => {
    // A scene that has already failed has been reported once; dropping
    // `sceneLoaded` on a lost context would otherwise re-arm this to say the
    // same thing again in a different voice.
    if (staticOnly || sceneLoaded || reportedRef.current) return undefined;

    const timer = setTimeout(() => {
      report(
        "The sky background scene never started",
        new Error(
          `Scene reported neither load nor error within ${SCENE_READY_TIMEOUT_MS}ms`
        )
      );
      setSceneFailed(true);
    }, SCENE_READY_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [staticOnly, sceneLoaded, report]);

  return (
    <div
      className={clsx(styles.sky, className)}
      data-testid="sky-background"
      data-scene-ready={sceneReady}
      aria-hidden="true"
    >
      {!staticOnly && (
        <div
          className={styles.still}
          data-testid="sky-still"
          data-shown={!sceneFailed}
        />
      )}
      <div ref={containerRef} className={styles.scene}>
        {!staticOnly && (
          <SceneBoundary onFailure={onSceneChunkFailure}>
            <Suspense fallback={null}>
              <UnicornScene
                width="100%"
                height="100%"
                fps={60}
                lazyLoad={false}
                onLoad={onLoad}
                onError={onError}
              />
            </Suspense>
          </SceneBoundary>
        )}
      </div>
    </div>
  );
};

export default SkyBackground;
